import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Search, RefreshCw, ArrowUpDown, ChevronDown, SlidersHorizontal, X, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import Button from '../components/ui/Button'
import { useViewportContext } from '../hooks/useViewportContext'
import {
  MarketViewSearch,
  ProductResultCard,
  ViewToggle,
  TradeoffsView,
  MapView,
  TechnologyTreeFilter,
  type MarketViewTab,
} from '../components/marketview'
import { resolveToLeafIds } from '../components/marketview/resolveToLeafIds'
import ScorecardFilterPanel, {
  ScorecardFilterCriteria,
  FilterScorecard,
} from '../components/scorecard/ScorecardFilterPanel'
import {
  api,
  MarketViewSearchResult,
  MarketViewDiscipline,
} from '../services/api'
import { tokenManager } from '../lib/api'
import { agentOverallValue, agentScoreValue } from '../lib/scoreDisplay'
import VendorDetailOverlay from '../components/VendorDetailOverlay'

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

type SortOption = 'overall' | 'need_fit' | 'technical' | 'semantic'

const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'overall', label: 'Overall Score' },
  { value: 'need_fit', label: 'Need Fit' },
  { value: 'technical', label: 'Technical' },
  { value: 'semantic', label: 'Semantic Match' },
]

/** Parse serialized scorecard response into FilterScorecard[] for ScorecardFilterPanel. */
function parseScorecardResponse(scorecard: any): FilterScorecard[] {
  if (!scorecard) return []
  return [{
    id: scorecard.id,
    name: scorecard.name,
    scorecard_type: scorecard.scorecard_type,
    scorecard_type_display: scorecard.scorecard_type_display || scorecard.scorecard_type,
    sections: (scorecard.sections || []).map((section: any) => ({
      id: section.id,
      title: section.title,
    })),
    items: (scorecard.sections || []).flatMap((section: any) =>
      (section.items || []).map((item: any) => ({
        id: item.id,
        title: item.title,
        description: item.description,
        importance: item.importance,
        spec_id: item.spec || null,
        spec_type: item.spec_type || null,
        spec_unit: item.spec_unit || null,
        typical_min: item.spec_typical_min != null ? Number(item.spec_typical_min) : null,
        typical_max: item.spec_typical_max != null ? Number(item.spec_typical_max) : null,
        lower_threshold: item.lower_threshold != null ? Number(item.lower_threshold) : null,
        upper_threshold: item.upper_threshold != null ? Number(item.upper_threshold) : null,
        higher_is_better: item.higher_is_better ?? true,
        section_id: section.id,
        spec_categories: item.spec_categories || [],
        acceptable_categories: item.acceptable_categories || [],
      }))
    ),
  }]
}

const MarketView: React.FC = () => {
  // URL params for view persistence
  const [searchParams, setSearchParams] = useSearchParams()

  // Search state — if deep link params are present, skip the welcome page immediately
  const hasDeepLink = Boolean(searchParams.get('tech') && searchParams.get('q'))
  const [query, setQuery] = useState(hasDeepLink ? searchParams.get('q')! : '')
  const [hasSearched, setHasSearched] = useState(hasDeepLink)
  const [isLoading, setIsLoading] = useState(hasDeepLink)
  const [error, setError] = useState<string | null>(null)
  const [selectedResult, setSelectedResult] = useState<MarketViewSearchResult | null>(null)
  const [selectedResultIndex, setSelectedResultIndex] = useState<number>(0)

  // Results state
  const [products, setProducts] = useState<MarketViewSearchResult[]>([])
  const [totalResults, setTotalResults] = useState(0)
  const [selectedTechnologyIds, setSelectedTechnologyIds] = useState<number[]>([])

  // Scorecard state (replaces specGroups/specCriteria)
  const [scorecardId, setScorecardId] = useState<number | null>(null)
  const [filterScorecards, setFilterScorecards] = useState<FilterScorecard[]>([])
  const [scorecardCriteria, setScorecardCriteria] = useState<ScorecardFilterCriteria>({})

  // Disciplines (for tech tree filter)
  const [allDisciplines, setAllDisciplines] = useState<MarketViewDiscipline[]>([])
  // Raw tech IDs from the last search response (before leaf resolution)
  const pendingTechIds = useRef<number[] | null>(null)

  // UI state
  const [currentView, setCurrentView] = useState<MarketViewTab>(
    (searchParams.get('view') as MarketViewTab) || 'products'
  )
  const [sortBy, setSortBy] = useState<SortOption>('overall')
  const [needsRefresh, setNeedsRefresh] = useState(false)
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const [filterPanelWidth, setFilterPanelWidth] = useState(320)
  const [isResizing, setIsResizing] = useState(false)
  const [showFilterDrawer, setShowFilterDrawer] = useState(false)
  const [filterCollapsed, setFilterCollapsed] = useState(false)

  // Pagination state
  const [searchId, setSearchId] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Tier 2: Register viewport serializer
  useViewportContext('market_view', () => ({
    query,
    has_searched: hasSearched,
    current_view: currentView,
    sort_by: sortBy,
    total_results: totalResults,
    selected_technologies: selectedTechnologyIds.length,
    scorecard_id: scorecardId,
    products: products.slice(0, 20).map(p => ({
      product_id: p.product?.id,
      product_name: p.product?.name,
      product_type: p.product?.product_type,
      vendor_id: p.vendor?.id,
      vendor_name: p.vendor?.name,
      vendor_location: p.vendor?.headquarters_location,
      is_small_business: p.vendor?.is_small_business,
      overall_score: agentOverallValue(p.scores),
      // 'N/A' / 'Not scored' rather than the placeholder number the agent
      // would otherwise quote as a real score (DEV-584).
      need_fit: agentScoreValue(p.scores?.need_fit),
      technical: agentScoreValue(p.scores?.technical),
      semantic_score: p.semantic_score,
    })),
  }), [query, hasSearched, currentView, sortBy, totalResults, products, selectedTechnologyIds, scorecardId])

  // Handle panel resize
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = e.clientX
      setFilterPanelWidth(Math.max(240, Math.min(500, newWidth)))
    }

    const handleMouseUp = () => {
      setIsResizing(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing])

  // Load disciplines on mount (for tech tree filter)
  useEffect(() => {
    const loadDisciplines = async () => {
      try {
        const response = await api.marketviewGetDisciplines()
        setAllDisciplines(response.disciplines)
      } catch (err) {
        console.error('Failed to load disciplines:', err)
      }
    }
    loadDisciplines()
  }, [])

  // Re-resolve pending tech IDs once the full discipline tree loads
  useEffect(() => {
    if (allDisciplines.length > 0 && pendingTechIds.current) {
      const resolved = resolveToLeafIds(pendingTechIds.current, allDisciplines)
      setSelectedTechnologyIds(resolved)
      pendingTechIds.current = null
    }
  }, [allDisciplines])

  // Main search function
  const handleSearch = useCallback(async (searchQuery: string) => {
    setQuery(searchQuery)
    setIsLoading(true)
    setError(null)
    setHasSearched(true)

    // New query → clear old scorecard and pagination
    setScorecardId(null)
    setScorecardCriteria({})
    setSearchId(null)
    setHasMore(false)

    try {
      const response = await api.marketviewSearch({
        query: searchQuery,
        technology_ids: selectedTechnologyIds.length > 0 ? selectedTechnologyIds : undefined,
        sort_by: sortBy,
      })

      setProducts(response.products)
      setTotalResults(response.total)
      setNeedsRefresh(false)
      setSearchId(response.search_id ?? null)
      setHasMore(response.has_more ?? false)

      // Store draft scorecard from response
      if (response.scorecard_id) {
        setScorecardId(response.scorecard_id)
        setFilterScorecards(parseScorecardResponse(response.scorecard))
      }

      // Auto-select technologies from results — resolve to leaf IDs using full tree
      const flatTechIds = response.tech_tree.disciplines.flatMap(d =>
        d.technologies.map(t => t.id)
      )
      pendingTechIds.current = flatTechIds
      setSelectedTechnologyIds(resolveToLeafIds(flatTechIds, allDisciplines))

    } catch (err) {
      console.error('Search failed:', err)
      setError(err instanceof Error ? err.message : 'Search failed')
    } finally {
      setIsLoading(false)
    }
  }, [selectedTechnologyIds, sortBy, allDisciplines])

  // Deep link support: ?tech=<id>&q=<query> triggers auto-search on mount
  const deepLinkHandled = useRef(false)
  useEffect(() => {
    if (deepLinkHandled.current) return
    const techParam = searchParams.get('tech')
    const queryParam = searchParams.get('q')
    if (techParam && queryParam) {
      deepLinkHandled.current = true
      const techId = parseInt(techParam, 10)
      if (!isNaN(techId)) {
        setSelectedTechnologyIds([techId])
        handleSearch(queryParam)
      }
    }
  }, [searchParams, handleSearch])

  // Refresh results (re-search with existing scorecard)
  const handleRefresh = useCallback(async () => {
    if (!query) return

    setIsLoading(true)
    setError(null)
    setSearchId(null)
    setHasMore(false)

    try {
      const response = await api.marketviewSearch({
        query,
        scorecard_id: scorecardId ?? undefined,
        technology_ids: selectedTechnologyIds.length > 0 ? selectedTechnologyIds : undefined,
        sort_by: sortBy,
      })

      setProducts(response.products)
      setTotalResults(response.total)
      setNeedsRefresh(false)
      setSearchId(response.search_id ?? null)
      setHasMore(response.has_more ?? false)

      // Update scorecard if a new one was created (e.g., tech filter changed product pool)
      if (response.scorecard_id) {
        setScorecardId(response.scorecard_id)
        setFilterScorecards(parseScorecardResponse(response.scorecard))
      }

    } catch (err) {
      console.error('Refresh failed:', err)
      setError(err instanceof Error ? err.message : 'Refresh failed')
    } finally {
      setIsLoading(false)
    }
  }, [query, scorecardId, selectedTechnologyIds, sortBy])

  // Handle sort change
  const handleSortChange = (newSort: SortOption) => {
    setSortBy(newSort)
    setShowSortDropdown(false)
    setNeedsRefresh(true)
  }

  // Handle view change
  const handleViewChange = (view: MarketViewTab) => {
    setCurrentView(view)
    const newParams = new URLSearchParams(searchParams)
    newParams.set('view', view)
    setSearchParams(newParams)
  }

  // Handle technology selection change from tree filter
  const handleTechSelectionChange = (techIds: number[]) => {
    setSelectedTechnologyIds(techIds)
    setNeedsRefresh(true)
  }

  // Inline search for results view
  const handleInlineSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      handleSearch(query.trim())
    }
  }

  // Load more results (infinite scroll)
  const loadMore = useCallback(async () => {
    if (!searchId || !hasMore || isLoadingMore) return
    setIsLoadingMore(true)
    try {
      const response = await api.marketviewFetchPage({
        search_id: searchId,
        offset: products.length,
      })
      setProducts(prev => [...prev, ...response.products])
      setHasMore(response.has_more ?? false)
    } catch {
      // Cache expired or error — stop loading more but keep existing results
      setHasMore(false)
    } finally {
      setIsLoadingMore(false)
    }
  }, [searchId, hasMore, isLoadingMore, products.length])

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMore()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [hasMore, isLoadingMore, loadMore])

  // Initial search screen
  if (!hasSearched) {
    return (
      <div className="min-h-full sm:h-full bg-background flex flex-col sm:flex-row overflow-hidden">
        <div className="flex-1 overflow-y-auto min-h-0">
          <MarketViewSearch
            onSearch={handleSearch}
            isLoading={isLoading}
          />
        </div>
      </div>
    )
  }

  // Filter panel content (shared between sidebar and mobile drawer)
  const filterPanelContent = (
    <div className="p-4 space-y-4">
      {/* Search Box */}
      <form onSubmit={handleInlineSearch} className="relative">
        <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
        <textarea
          ref={(el) => {
            if (el) {
              el.style.height = 'auto'
              const lineHeight = parseInt(getComputedStyle(el).lineHeight) || 20
              el.style.height = `${Math.min(el.scrollHeight, lineHeight * 4)}px`
            }
          }}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setNeedsRefresh(true)
            // Auto-resize
            const el = e.target
            el.style.height = 'auto'
            const lineHeight = parseInt(getComputedStyle(el).lineHeight) || 20
            el.style.height = `${Math.min(el.scrollHeight, lineHeight * 4)}px`
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              if (query.trim()) handleSearch(query.trim())
            }
          }}
          placeholder="Refine search..."
          className="w-full pl-9 pr-4 py-2 text-sm rounded-md border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none overflow-hidden"
          rows={1}
        />
      </form>

      {/* Update Button */}
      {needsRefresh && !isLoading && (
        <Button
          onClick={handleRefresh}
          disabled={isLoading}
          className="w-full flex items-center justify-center gap-2"
          variant="default"
        >
          <RefreshCw className="w-4 h-4" />
          Update Results
        </Button>
      )}

      {/* Scorecard Filter Panel */}
      <ScorecardFilterPanel
        scorecards={filterScorecards}
        criteria={scorecardCriteria}
        onCriteriaChange={(newCriteria) => {
          setScorecardCriteria(newCriteria)
          if (hasSearched && products.length > 0) {
            setNeedsRefresh(true)
          }
        }}
        onItemUpdate={async (itemId, updates) => {
          try {
            const token = tokenManager.getAccessToken()
            await fetch(`${API_BASE_URL}/api/scorecard-items/${itemId}/`, {
              method: 'PATCH',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(updates),
            })
          } catch (err) {
            console.error('Failed to persist scorecard item update:', err)
          }
        }}
        onItemAdd={async (scorecardId, sectionId, { title, importance }) => {
              try {
                const token = tokenManager.getAccessToken()
                const res = await fetch(`${API_BASE_URL}/api/scorecard-items/`, {
                  method: 'POST',
                  headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    section: sectionId,
                    title,
                    description: '',
                    importance,
                    order: 999,
                    requires_llm_scoring: false,
                  }),
                })
                if (res.ok) {
                  const newItem = await res.json()
                  setFilterScorecards(prev =>
                    prev.map(sc =>
                      sc.id === scorecardId
                        ? {
                            ...sc,
                            items: [...sc.items, {
                              id: newItem.id,
                              title: newItem.title,
                              description: newItem.description || '',
                              importance: newItem.importance,
                              spec_id: newItem.spec || null,
                              spec_type: newItem.spec_type || null,
                              spec_unit: newItem.spec_unit || null,
                              typical_min: null,
                              typical_max: null,
                              lower_threshold: null,
                              upper_threshold: null,
                              higher_is_better: true,
                              section_id: sectionId,
                              spec_categories: newItem.spec_categories || [],
                              acceptable_categories: newItem.acceptable_categories || [],
                            }],
                          }
                        : sc
                    )
                  )
                  setNeedsRefresh(true)
                }
              } catch (err) {
                console.error('Failed to add scorecard item:', err)
              }
            }}
      />

      {/* Technology Tree Filter */}
      <TechnologyTreeFilter
        allDisciplines={allDisciplines}
        selectedTechnologyIds={selectedTechnologyIds}
        onSelectionChange={handleTechSelectionChange}
      />
    </div>
  )

  // Results view
  return (
    <div className="h-full bg-background flex overflow-hidden">
      {/* Filter Panel - Sidebar on md+, drawer on mobile */}
      <>
        {/* Desktop: resizable sidebar */}
        <aside
          className="hidden md:flex flex-col border-r border-border bg-card flex-shrink-0 h-full relative"
          style={{ width: filterCollapsed ? 0 : filterPanelWidth }}
        >
          {!filterCollapsed && (
            <div className="flex-1 overflow-y-auto">
              {filterPanelContent}
            </div>
          )}
          {/* Collapse/expand toggle pinned to bottom-right */}
          <button
            onClick={() => setFilterCollapsed(!filterCollapsed)}
            className="absolute bottom-3 -right-3 z-10 p-1 rounded-full bg-card border border-border shadow-sm hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title={filterCollapsed ? 'Expand filters' : 'Collapse filters'}
            aria-label={filterCollapsed ? 'Expand filters' : 'Collapse filters'}
          >
            {filterCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          </button>
        </aside>

        {/* Mobile: overlay drawer */}
        {showFilterDrawer && (
          <>
            <div
              className="fixed inset-0 z-20 bg-black/50 md:hidden"
              onClick={() => setShowFilterDrawer(false)}
              aria-hidden="true"
            />
            <aside className="fixed top-[var(--cui-banner-top)] left-0 right-0 z-30 w-full max-h-[85vh] flex flex-col overflow-hidden bg-card border-b border-border shadow-lg rounded-b-md md:hidden">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card flex-shrink-0">
                <span className="text-sm font-semibold text-foreground">Filters</span>
                <button
                  onClick={() => setShowFilterDrawer(false)}
                  className="p-2 rounded-md hover:bg-muted text-muted-foreground"
                  aria-label="Close filters"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                {filterPanelContent}
              </div>
            </aside>
          </>
        )}
      </>

      {/* Resize Handle */}
      {!filterCollapsed && (
        <div
          className={`hidden md:block w-1.5 hover:w-2 bg-border/50 hover:bg-primary/60 cursor-col-resize flex-shrink-0 transition-all ${
            isResizing ? 'w-2 bg-primary' : ''
          }`}
          onMouseDown={handleMouseDown}
          title="Drag to resize"
        />
      )}

      {/* Main Content - Results */}
      <main className="flex-1 min-w-0 h-full flex flex-col relative">
        {/* Results Header */}
        <div className="sticky top-0 z-10 bg-background border-b border-border px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                {isLoading ? (
                  <h1 className="text-lg sm:text-xl font-semibold text-muted-foreground truncate">
                    Searching…
                  </h1>
                ) : (
                  <h1 className="text-lg sm:text-xl font-semibold text-foreground truncate">
                    {products.length} of {totalResults} candidates
                  </h1>
                )}
              </div>
              {/* Mobile: Filters button */}
              <button
                onClick={() => setShowFilterDrawer(true)}
                className="md:hidden p-2.5 rounded-md border border-border bg-card hover:bg-muted flex-shrink-0"
                title="Filters"
                aria-label="Open filters"
              >
                <SlidersHorizontal className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <ViewToggle currentView={currentView} onChange={handleViewChange} />

              {currentView === 'products' && (
                <div className="relative">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                    className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm"
                  >
                    <ArrowUpDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="truncate max-w-[120px] sm:max-w-none">
                      {sortOptions.find(o => o.value === sortBy)?.label}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  </Button>

                  {showSortDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-md shadow-md z-20 overflow-hidden">
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleSortChange(option.value)}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-accent
                                    ${sortBy === option.value ? 'text-foreground font-medium' : 'text-muted-foreground'}`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Results Content */}
        {error ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={() => handleSearch(query)}>Retry Search</Button>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-muted-foreground text-sm">
                Searching and scoring products against your criteria...
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 justify-items-stretch">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="w-full min-w-0 bg-card border border-border rounded-md overflow-hidden animate-pulse">
                  <div className="h-40 bg-muted" />
                  <div className="p-4 space-y-3">
                    <div className="h-5 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                    <div className="h-16 bg-muted rounded" />
                    <div className="flex justify-center py-4">
                      <div className="w-32 h-32 bg-muted rounded-full" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : products.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <p className="text-muted-foreground mb-4">No products found matching your search.</p>
              <p className="text-sm text-muted-foreground">
                Try adjusting your search terms or criteria.
              </p>
            </div>
          </div>
        ) : (
          <>
            {currentView === 'products' && (
              <div className="flex-1 p-4 sm:p-6 overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5 justify-items-stretch">
                  {products.map((result, index) => (
                    <div key={result.product.id} className="w-full min-w-0">
                      <ProductResultCard
                        result={result}
                        onClick={() => {
                          setSelectedResult(result)
                          setSelectedResultIndex(index)
                        }}
                      />
                    </div>
                  ))}
                </div>
                {/* Infinite scroll sentinel */}
                {hasMore && (
                  <div ref={sentinelRef} className="flex justify-center py-8">
                    {isLoadingMore && (
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                        <span className="text-sm text-muted-foreground">Loading more products...</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {currentView === 'tradeoffs' && (
              <div className="flex-1 relative min-h-0">
                <TradeoffsView results={products} />
              </div>
            )}

            {currentView === 'map' && (
              <div className="flex-1 relative min-h-0">
                <MapView results={products} />
              </div>
            )}
          </>
        )}
      </main>

      {/* Vendor Detail Overlay */}
      {selectedResult && (
        <VendorDetailOverlay
          result={selectedResult}
          onClose={() => setSelectedResult(null)}
          allResults={products}
          currentIndex={selectedResultIndex}
          onNavigate={(index) => {
            setSelectedResult(products[index])
            setSelectedResultIndex(index)
          }}
        />
      )}
    </div>
  )
}

export default MarketView
